import { Injectable } from '@angular/core';
import { chunk } from 'lodash-es';
import moment from 'moment';
import { concat, forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { CacheService } from '@nimbus-library/features/cache/index';
import { RequestAggregatorService } from './services/api/http-strategy/request-aggregator.service';

@Injectable({ providedIn: 'root' })
export class ForkRequestsService {
  private readonly BATCH_SIZE_CACHE_KEY = 'BatchSize';
  private readonly DEFAULT_BATCH_SIZE = 40;
  private readonly defaultBatchSize;

  public constructor(
    private readonly requestAggregator: RequestAggregatorService,
    cacheService: CacheService,
  ) {
    const entityCache = cacheService.for<number>('ForkRequestsService', { cacheMaxAge: moment.duration(1, 'years') });
    const cacheInfo = entityCache.getInfo(this.BATCH_SIZE_CACHE_KEY);
    if (cacheInfo && cacheInfo.validForCache && cacheInfo.value) {
      this.defaultBatchSize = cacheInfo.value;
    } else {
      entityCache.set(this.BATCH_SIZE_CACHE_KEY, (this.defaultBatchSize = this.DEFAULT_BATCH_SIZE));
    }
  }

  public forkRequests<TResult>(
    resourceIds: string[],
    getResult$: (resourceIds: string[]) => Observable<TResult>,
    batchSize?: number,
    useRequestAggregator: boolean = true,
  ): Observable<TResult[]> {
    if (!batchSize) {
      batchSize = this.defaultBatchSize;
    }

    if (resourceIds.length < batchSize) {
      return getResult$(resourceIds).pipe(map((result) => [result]));
    }

    return this.makeRequests(resourceIds, getResult$, batchSize, useRequestAggregator);
  }

  public forkAndConcatRequest<TResult>(
    resources: any[],
    getResult$: (resources: any[]) => Observable<TResult>,
    batchSize?: number,
  ): Observable<TResult> {
    if (!batchSize) {
      batchSize = this.defaultBatchSize;
    }
    if (resources.length < batchSize) {
      return getResult$(resources);
    }
    const chunks = chunk(resources, batchSize);
    return concat(...chunks.map(getResult$));
  }

  public forkArrayRequests<TResult>(
    resourceIds: string[],
    getResult$: (resourceIds: string[]) => Observable<TResult[]>,
    batchSize?: number,
    useRequestAggregator: boolean = true,
  ): Observable<TResult[]> {
    if (!batchSize) {
      batchSize = this.defaultBatchSize;
    }

    if (resourceIds.length < batchSize) {
      return getResult$(resourceIds);
    }

    return this.makeRequests(resourceIds, getResult$, batchSize, useRequestAggregator).pipe(map((results) => this.mergeArrays(results)));
  }

  public forkMapRequests<TResult>(
    resourceIds: string[],
    getResult$: (resourceIds: string[]) => Observable<IMap<TResult>>,
    batchSize?: number,
    useRequestAggregator: boolean = true,
  ): Observable<IMap<TResult>> {
    if (!batchSize) {
      batchSize = this.defaultBatchSize;
    }

    if (resourceIds.length < batchSize) {
      return getResult$(resourceIds);
    }

    return this.makeRequests(resourceIds, getResult$, batchSize, useRequestAggregator).pipe(map((results) => this.mergeMaps(results)));
  }

  private makeRequests<TResult>(
    resourceIds: string[],
    getResult$: (resourceIds: string[]) => Observable<TResult>,
    batchSize: number,
    useRequestAggregator: boolean,
  ): Observable<TResult[]> {
    const batches = chunk(resourceIds, batchSize);
    if (!useRequestAggregator || this.requestAggregator.isAggregating()) {
      return forkJoin(batches.map((resourceIds) => getResult$(resourceIds)));
    }

    return this.requestAggregator.aggregatedRequest(() => forkJoin(batches.map((resourceIds) => getResult$(resourceIds))));
  }

  private mergeArrays<TResult>(arrays: TResult[][]): TResult[] {
    const aggregatedResult: TResult[] = [];

    for (const arr of arrays) {
      for (const result of arr) {
        aggregatedResult.push(result);
      }
    }

    return aggregatedResult;
  }

  private mergeMaps<TResult>(maps: IMap<TResult>[]): IMap<TResult> {
    const aggregatedResult: IMap<TResult> = {};

    for (const map of maps) {
      Object.assign(aggregatedResult, map);
    }

    return aggregatedResult;
  }
}
